/**
 * Groups "achiev", "task", "present", "tribute" and "grave" of the progress ops: claiming achievement and task
 * bonuses, the daily present, the tribute of captured assault zones and searching the graves after a won battle.
 *
 * SERVER-SUPPLIED ARGUMENTS. Some of these ops depend on facts only the server knows (the original kept them in the
 * client's memory or fetched them from its server): present.claim `available` (today's present of session.enter),
 * tribute.claim `playerId` / `zones` / `clanMembers` / `serverTime` (the assault service), grave.search `battle`
 * (the graves of the last verified, won battle). The command service fills these fields from its own state and never
 * takes them from the client's request; the rules only validate their shape and apply the original formulas.
 */
import { argsObject, boolArg, intArg, optionalIntArg, stringArg } from '../args';
import { int, keys } from '../as3';
import type { RuleContext } from '../context';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import type { PlayerState } from '../state';
import { checkNewLevel, newEndGame } from './battle-level';
import { achievBonus, levelUpResult, syncLevel, taskBonus, type LevelUpResult } from './progress-common';
import { achievLevel, achievRank, weaponOptimisation } from './shop-common';

export interface ClaimResult {
  gold: number;
  diam: number;
  exp: number;
  /** The level-up the claim caused (levels 0 = none). */
  levelUp: LevelUpResult;
}

// ------------------------------------------------------------------------------------------------ present

/** close_present rewards by login streak (new_interf.ts:5592-5630): tween 1..8 -> gold, 9 -> 1 diamond, >= 10 -> 5 skill points. */
export const PRESENT_GOLD: Readonly<Record<number, number>> = { 1: 50, 2: 100, 3: 150, 4: 200, 5: 250, 6: 300, 7: 400, 8: 500 };

// ------------------------------------------------------------------------------------------------ tribute

export interface TributeZone {
  /** Assault zone 1..17. */
  zone: number;
  /** Backend player id of the owner (null = nobody). */
  ownerId: string | null;
  /** Unix seconds of the last capture (ASSAULT[z].last_assault). */
  lastCaptureAt: number;
}

export interface TributeClaimArgs {
  /** The player's backend id (FWP.user.user_id). */
  playerId: string;
  zones: TributeZone[];
  /** Backend ids of the members of the player's clan (the server's clan index); empty = no clan. */
  clanMembers: string[];
  /** The assault service's time (FWP.assault_cur_time = fwproject_time); default ctx.now. */
  serverTime?: number;
}

export interface TributeResult {
  terr_total: number;
  my_gold: number;
  clans_gold: number;
  gold: number;
  next_assault_payment: number;
}

export const ASSAULT_ZONES = 17;

/** FWP.get_tribute + get_tribute_coeff (FWP.ts:7040-7075). */
function getTribute(ctx: RuleContext, args: TributeClaimArgs, time: number): { terr_total: number; my_gold: number; clans_gold: number } {
  const byZone = new Map<number, TributeZone>();
  for (const z of args.zones) byZone.set(z.zone, z);
  const coeff = (z: TributeZone) => {
    const d = time - z.lastCaptureAt;
    if (d > 60 * 60) return 1;
    return d / 3600;
  };
  let terr = 0;
  let mine = 0;
  for (let z = 1; z <= ASSAULT_ZONES; z++) {
    const info = byZone.get(z);
    if (info && info.ownerId !== null && info.ownerId === args.playerId) {
      terr++;
      mine += Math.ceil(ctx.defs.TEAMS_DEF['assault' + z].payments * coeff(info));
    }
  }
  let clan = 0;
  if (args.clanMembers.length > 0) {
    for (let z = 1; z <= ASSAULT_ZONES; z++) {
      const info = byZone.get(z);
      if (info && info.ownerId !== null && args.clanMembers.includes(info.ownerId) && info.ownerId !== args.playerId) {
        clan += Math.ceil((ctx.defs.TEAMS_DEF['assault' + z].payments * coeff(info)) / 10);
      }
    }
  }
  return { terr_total: terr, my_gold: mine, clans_gold: clan };
}

// ------------------------------------------------------------------------------------------------ graves

export interface Grave {
  /** Worm name shown on the button. */
  name: string;
  /** cpu_level of the dead enemy's team (1..7). */
  cpu_level: number;
  /** Already searched (graves_array[n].clicked). */
  searched?: boolean;
}

export interface GraveBattle {
  /** end_game.game_mode of the battle. */
  mode: string;
  /** end_game.game_stage. */
  stage: number;
  /** end_game.graves[1..n]: the enemy 'G' objects at the end of the battle (FWP.end_game_fn). */
  graves: Grave[];
}

export interface GraveSearchArgs {
  /** Grave number 1..16 (the gsearch_<n> button). */
  index: number;
  battle: GraveBattle;
  /**
   * Port addition (the opt-in rewarded ad 'ad_grave_extra', src/server/core/services/ads.ts): a bonus roll of a grave
   * that was searched already: a diamond result becomes the gold result of the same roll, and it does not tire.
   */
  adBonus?: boolean;
}

export interface GraveResult {
  index: number;
  /** '' (nothing), 'stim', 'gold', 'diam', 'obj' or 'weapon'. */
  ptype: string;
  id: string;
}

/** The grave search screen has 16 buttons (new_interf.init_grave_search). */
export const MAX_GRAVES = 16;
/** finish_grave_search's hardcoded cap for objects and stimulators (not OBJ_DEF.limit). */
export const GRAVE_ITEM_CAP = 50;

const GRAVE_MODES = ['career', 'missions', 'survival', 'boss'] as const;

/** FWP.get_grave_result (FWP.ts:6464-6772), line by line (true_random(1, 1000), true_random(1, 100), true_random(1, n)). */
function getGraveResult(ctx: RuleContext, cpuLevel: number, mode: string, stage: number, noDiamonds = false): { ptype: string; id: string } {
  const out = { ptype: '', id: '' };
  const r1 = ctx.rng.int(1, 1000);
  if (mode == 'boss') {
    if (r1 <= 100) out.ptype = 'gold';
    else if (r1 <= 600) out.ptype = 'diam';
    else out.ptype = 'obj';
  } else if (cpuLevel == 7) {
    if (r1 <= 300) out.ptype = 'stim';
    else if (r1 <= 600) out.ptype = 'gold';
    else if (r1 <= 650) out.ptype = 'diam';
    else out.ptype = 'obj';
  } else if (cpuLevel == 6) {
    if (r1 <= 400) out.ptype = 'stim';
    else if (r1 <= 600) out.ptype = 'gold';
    else if (r1 <= 650) out.ptype = 'diam';
    else if (r1 <= 900) out.ptype = 'obj';
    else out.ptype = '';
  } else if (cpuLevel == 5) {
    if (r1 <= 440) out.ptype = 'stim';
    else if (r1 <= 690) out.ptype = 'gold';
    else if (r1 <= 700) out.ptype = 'diam';
    else if (r1 <= 850) out.ptype = 'obj';
    else out.ptype = '';
  } else if (cpuLevel == 4) {
    if (r1 <= 400) out.ptype = 'stim';
    else if (r1 <= 550) out.ptype = 'gold';
    else if (r1 <= 600) out.ptype = 'weapon';
    else if (r1 <= 750) out.ptype = 'obj';
    else out.ptype = '';
  } else if (cpuLevel == 3) {
    if (r1 <= 350) out.ptype = 'stim';
    else if (r1 <= 450) out.ptype = 'gold';
    else if (r1 <= 550) out.ptype = 'weapon';
    else if (r1 <= 700) out.ptype = 'obj';
    else out.ptype = '';
  } else if (cpuLevel == 2) {
    if (r1 <= 300) out.ptype = 'stim';
    else if (r1 <= 350) out.ptype = 'gold';
    else if (r1 <= 450) out.ptype = 'weapon';
    else if (r1 <= 600) out.ptype = 'obj';
    else out.ptype = '';
  } else if (cpuLevel == 1) {
    if (r1 <= 250) out.ptype = 'stim';
    else if (r1 <= 350) out.ptype = 'gold';
    else if (r1 <= 500) out.ptype = 'obj';
    else out.ptype = 'weapon';
  }
  if (out.ptype == '') return out;
  // PORT: the ad bonus roll (GraveSearchArgs.adBonus) gives the gold of the same roll instead of diamonds.
  if (noDiamonds && out.ptype == 'diam') out.ptype = 'gold';
  const r2 = ctx.rng.int(1, 100);
  const rich = mode == 'boss' || cpuLevel == 7 || cpuLevel == 6;
  if (out.ptype == 'stim') {
    if (rich) {
      if (r2 <= 50) out.id = 'stimpack';
      else if (r2 <= 65) out.id = 'jet';
      else if (r2 <= 80) out.id = 'buffout';
      else if (r2 <= 92) out.id = 'superstimpack';
      else out.id = 'psycho';
    } else if (cpuLevel >= 4) {
      if (r2 <= 70) out.id = 'stimpack';
      else if (r2 <= 82) out.id = 'jet';
      else if (r2 <= 94) out.id = 'buffout';
      else out.id = 'superstimpack';
    } else if (r2 <= 80) out.id = 'stimpack';
    else if (r2 <= 90) out.id = 'jet';
    else out.id = 'buffout';
    return out;
  }
  if (out.ptype == 'gold') {
    if (rich) {
      if (r2 <= 50) out.id = '300';
      else if (r2 <= 70) out.id = '400';
      else if (r2 <= 80) out.id = '600';
      else if (r2 <= 90) out.id = '800';
      else if (r2 <= 95) out.id = '1000';
      else out.id = '1500';
    } else if (cpuLevel >= 4) {
      if (r2 <= 50) out.id = '100';
      else if (r2 <= 80) out.id = '200';
      else if (r2 <= 90) out.id = '300';
      else if (r2 <= 95) out.id = '400';
      else out.id = '500';
    } else if (r2 <= 80) out.id = '50';
    else if (r2 <= 90) out.id = '100';
    else if (r2 <= 95) out.id = '150';
    else out.id = '200';
    return out;
  }
  if (out.ptype == 'diam') {
    if (mode == 'boss' || cpuLevel == 6) {
      if (r2 <= 95) out.id = '2';
      else if (r2 <= 98) out.id = '3';
      else out.id = '4';
    } else if (cpuLevel >= 4) {
      if (r2 <= 95) out.id = '1';
      else out.id = '2';
    }
    return out;
  }
  if (out.ptype == 'weapon') {
    let team: any;
    if (mode == 'career') team = ctx.defs.TEAMS_DEF['career' + stage];
    if (mode == 'missions') team = ctx.defs.TEAMS_DEF['mission' + stage];
    if (mode == 'survival') team = ctx.defs.TEAMS_DEF['survival' + stage];
    if (mode == 'boss') team = ctx.defs.TEAMS_DEF['boss' + stage];
    const W = ctx.defs.WEAPONS_DEF;
    const pool: any[] = [0, ''];
    for (let t = 2; t <= team.teams + 1; t++) {
      for (const k of keys(team['team' + t].weapons)) {
        const key = k as string;
        if (W[key].id < 200 && W[key].wpclass <= 3 && key != 'skip_go') {
          let dup = false;
          for (let i = 1; i <= pool[0]; i++) if (pool[i] == key) dup = true;
          if (!dup) {
            ++pool[0];
            pool[pool[0]] = key;
          }
        }
      }
    }
    if (pool[0] == 0) return { ptype: '', id: '' };
    out.id = pool[ctx.rng.int(1, pool[0])];
  }
  if (out.ptype == 'obj') {
    if (mode == 'boss' || cpuLevel == 7) {
      if (r2 <= 5) out.id = 'obj_barrel';
      else if (r2 <= 10) out.id = 'obj_middle_clip';
      else if (r2 <= 15) out.id = 'obj_small_cell';
      else if (r2 <= 20) out.id = 'obj_napalm_cont';
      else if (r2 <= 25) out.id = 'obj_laser_mod';
      else if (r2 <= 30) out.id = 'obj_plasma_mod';
      else if (r2 <= 35) out.id = 'obj_fuel';
      else if (r2 <= 40) out.id = 'obj_small_expl';
      else if (r2 <= 52) out.id = 'obj_big_clip';
      else if (r2 <= 64) out.id = 'obj_big_cell';
      else if (r2 <= 76) out.id = 'obj_hard_jacket';
      else if (r2 <= 88) out.id = 'obj_poison_cont';
      else out.id = 'obj_coil';
    } else if (r2 <= 15) out.id = 'obj_barrel';
    else if (r2 <= 30) out.id = 'obj_middle_clip';
    else if (r2 <= 45) out.id = 'obj_small_cell';
    else if (r2 <= 50) out.id = 'obj_napalm_cont';
    else if (r2 <= 55) out.id = 'obj_laser_mod';
    else if (r2 <= 60) out.id = 'obj_plasma_mod';
    else if (r2 <= 75) out.id = 'obj_fuel';
    else if (r2 <= 90) out.id = 'obj_small_expl';
    else if (r2 <= 92) out.id = 'obj_big_clip';
    else if (r2 <= 94) out.id = 'obj_big_cell';
    else if (r2 <= 96) out.id = 'obj_hard_jacket';
    else if (r2 <= 98) out.id = 'obj_poison_cont';
    else out.id = 'obj_coil';
    return out;
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ helpers

function parseIdString(o: Record<string, unknown>, field: string): string {
  return stringArg(o, field, { maxLength: 128, pattern: /^[\x21-\x7e]+$/ });
}

function applyClaimLevelUp(p: PlayerState, ctx: RuleContext): LevelUpResult {
  const endGame = newEndGame();
  // checkNewLevel also stores the level derived from exp: new_interf.init_new_level (7662) sets it after the save.
  checkNewLevel(p, endGame, ctx);
  return levelUpResult(endGame);
}

export const claimOps = {
  /**
   * achiev.claim: 'get_achiev_bonus' (new_interf.ts:4617-4660), visible while get_achiev_bonus().exist (render 3212).
   * gold/diam += the prizes of every newly reached achievement level, exp += round((LT[level + 1] - LT[level]) x
   * sum% / 100), last_achiev = current levels, last_achiev_range = rank, achiev_rating_bonus = ACHIEV_RATING_BONUS[rank]
   * (an absolute value; rating itself is not changed), then check_new_level (surprise perk draws on a level-up).
   * Stale-level fix: the level is derived from exp first (like net_initialized), so the exp prize uses the real level
   * and check_new_level does not grant the levels of an earlier level-up a second time.
   */
  'achiev.claim': defineOp<Record<string, never>, ClaimResult>({
    parseArgs(raw) {
      argsObject(raw, []);
      return {};
    },
    run(state, _args, ctx) {
      const p = state.player;
      const LT = ctx.defs.LEVEL_THRESHOLD;
      syncLevel(p, ctx.defs);
      const bonus = achievBonus(p, ctx.defs);
      if (!bonus.exist) throw new RuleError('not_available', { problem: 'no achievement bonus' });
      let exp = 0;
      if (bonus.gold != 0) p.gold = int(p.gold + bonus.gold);
      if (bonus.diam != 0) p.diam = int(p.diam + bonus.diam);
      if (bonus.exp != 0) {
        exp = Math.round(((LT[p.level + 1] - LT[p.level]) * bonus.exp) / 100);
        p.exp = int(p.exp + exp);
      }
      for (const k of keys(p.achiev)) p.last_achiev[k as string] = achievLevel(ctx.defs, k, p.achiev[k as string]);
      p.last_achiev_range = achievRank(ctx.defs, p.achiev);
      p.achiev_rating_bonus = ctx.defs.ACHIEV_RATING_BONUS[p.last_achiev_range];
      const levelUp = applyClaimLevelUp(p, ctx);
      return { state, result: { gold: bonus.gold, diam: bonus.diam, exp, levelUp } };
    },
  }),

  /**
   * task.claim: 'get_task_bonus' (new_interf.ts:4661-4699), visible while get_task_bonus().exist (render 4127): the raw
   * gold/diam/exp bonuses of tasks last_presented_task .. cur_task - 1, last_presented_task = cur_task, then
   * check_new_level (level derived from exp first, as in achiev.claim).
   */
  'task.claim': defineOp<Record<string, never>, ClaimResult>({
    parseArgs(raw) {
      argsObject(raw, []);
      return {};
    },
    run(state, _args, ctx) {
      const p = state.player;
      syncLevel(p, ctx.defs);
      const bonus = taskBonus(p, ctx.defs);
      if (!bonus.exist) throw new RuleError('not_available', { problem: 'no task bonus' });
      if (bonus.gold != 0) p.gold = int(p.gold + bonus.gold);
      if (bonus.diam != 0) p.diam = int(p.diam + bonus.diam);
      if (bonus.exp != 0) p.exp = int(p.exp + bonus.exp);
      p.last_presented_task = p.cur_task;
      const levelUp = applyClaimLevelUp(p, ctx);
      return { state, result: { gold: bonus.gold, diam: bonus.diam, exp: bonus.exp, levelUp } };
    },
  }),
};

/**
 * Claims with server-supplied inputs (see the file comment): registered in SERVER_RULE_OPS, so player.command cannot
 * reach them with client-made arguments; the command service runs them with the facts it owns.
 */
export const claimServerOps = {
  /**
   * present.claim { available }: 'close_present' (new_interf.ts:5592-5630) of the daily present screen, which the
   * original showed once per session when modify_last_enter found a new day (get_present != 0) and the tutorial was
   * done (new_interf.ts:254-256). `available` is the server's "today's present not claimed yet" flag (session.enter's
   * presentAvailable). The multiplier of the original social build (x2 for the author's friends) is gone: always x1.
   */
  'present.claim': defineOp<{ available: boolean }, { tween: number; gold: number; diam: number; skill_pts: number }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['available']);
      return { available: boolArg(o, 'available') };
    },
    run(state, { available }) {
      const p = state.player;
      if (!available || p.view_tutorial != 1) throw new RuleError('not_available', { problem: 'no present today' });
      const tween = p.tween;
      const got = { tween, gold: 0, diam: 0, skill_pts: 0 };
      if (Object.prototype.hasOwnProperty.call(PRESENT_GOLD, tween) && Number.isInteger(tween)) {
        got.gold = PRESENT_GOLD[tween];
        p.gold = int(p.gold + got.gold);
      }
      if (p.tween == 9) {
        got.diam = 1;
        p.diam = int(p.diam + 1);
      }
      if (p.tween >= 10) {
        got.skill_pts = 5;
        p.skill_pts = int(p.skill_pts + 5);
        p.tween = 1;
      }
      return { state, result: got };
    },
  }),

  /**
   * tribute.claim { playerId, zones, clanMembers, serverTime? } (all server-supplied): 'finish_tribute'
   * (new_interf.ts:5229-5238) with FWP.get_tribute / get_tribute_coeff (FWP.ts:7040-7075): gold += sum over own zones
   * of ceil(payments x coeff) + sum over the clan's zones of ceil(payments x coeff / 10), coeff = min(1, (time -
   * last capture) / 3600); next_assault_payment = time + 3600.
   * Preconditions (render 4081-4110): the tribute clip is shown only when next_assault_payment == 0 or has passed, and
   * finish_tribute only when the sum is > 0.
   */
  'tribute.claim': defineOp<TributeClaimArgs, TributeResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['playerId', 'zones', 'clanMembers', 'serverTime']);
      const playerId = parseIdString(o, 'playerId');
      if (!Array.isArray(o.zones) || o.zones.length > ASSAULT_ZONES) throw new RuleError('bad_args', { field: 'zones', problem: `must be an array of at most ${ASSAULT_ZONES} zones` });
      const seen = new Set<number>();
      const zones = o.zones.map((z, i) => {
        const zo = argsObject(z, ['zone', 'ownerId', 'lastCaptureAt']);
        const zone = intArg(zo, 'zone', { min: 1, max: ASSAULT_ZONES });
        if (seen.has(zone)) throw new RuleError('bad_args', { field: `zones.${i}.zone`, problem: 'duplicate zone' });
        seen.add(zone);
        const ownerId = zo.ownerId === null || zo.ownerId === undefined ? null : parseIdString(zo, 'ownerId');
        return { zone, ownerId, lastCaptureAt: intArg(zo, 'lastCaptureAt', { min: 0 }) };
      });
      if (!Array.isArray(o.clanMembers) || o.clanMembers.length > 10_000) throw new RuleError('bad_args', { field: 'clanMembers', problem: 'must be an array of player ids' });
      const clanMembers = o.clanMembers.map((m, i) => parseIdString({ [`clanMembers.${i}`]: m }, `clanMembers.${i}`));
      const serverTime = optionalIntArg(o, 'serverTime', { min: 0 });
      return { playerId, zones, clanMembers, ...(serverTime !== undefined ? { serverTime } : {}) };
    },
    run(state, args, ctx) {
      const p = state.player;
      const time = args.serverTime ?? ctx.now;
      if (!(p.next_assault_payment == 0 || p.next_assault_payment < time)) throw new RuleError('not_available', { problem: 'tribute not due yet', next_assault_payment: p.next_assault_payment });
      const t = getTribute(ctx, args, time);
      const sum = t.my_gold + t.clans_gold;
      if (!(sum > 0)) throw new RuleError('not_available', { problem: 'nothing to collect' });
      p.gold = int(p.gold + sum);
      p.next_assault_payment = time + 60 * 60;
      return { state, result: { ...t, gold: sum, next_assault_payment: p.next_assault_payment } };
    },
  }),

  /**
   * grave.search { index, battle } (`battle` server-supplied): 'gsearch_button' of the grave search screen
   * (new_interf.ts:5518-5527) -> finish_grave_search (7778-7820) with FWP.get_grave_result (FWP.ts:6464-6772).
   * Preconditions: the screen exists only after a won battle that is not an assault (init_grave_search 7731-7755)
   * and not a 'just' battle (no rewards on the own backend; the original let its weapon draw crash there), the
   * grave exists (at most 16 buttons) and was not searched yet (graves_array[n].clicked). A weapon is added up to
   * WEAPONS_DEF.limit (+ weapon_optimisation), an object or a stimulator only below 50 (hardcoded, not OBJ_DEF.limit),
   * gold/diamonds are added, tired += 3; an empty grave changes nothing (no tired, no save in the original).
   */
  'grave.search': defineOp<GraveSearchArgs, GraveResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['index', 'battle', 'adBonus']);
      const index = intArg(o, 'index', { min: 1, max: MAX_GRAVES });
      const adBonus = o.adBonus === undefined ? false : boolArg(o, 'adBonus');
      if (o.battle === undefined || o.battle === null) throw new RuleError('bad_args', { field: 'battle', problem: 'required' });
      const b = argsObject(o.battle, ['mode', 'stage', 'graves']);
      const mode = stringArg(b, 'mode', { maxLength: 16, pattern: /^[a-z]+$/ });
      const stage = intArg(b, 'stage', { min: 0, max: 1000 });
      if (!Array.isArray(b.graves) || b.graves.length > 64) throw new RuleError('bad_args', { field: 'battle.graves', problem: 'must be an array' });
      const graves = b.graves.map((g) => {
        const go = argsObject(g, ['name', 'cpu_level', 'searched']);
        return {
          name: stringArg(go, 'name', { maxLength: 64 }),
          cpu_level: intArg(go, 'cpu_level', { min: 0, max: 100 }),
          searched: go.searched === undefined ? false : boolArg(go, 'searched'),
        };
      });
      return { index, battle: { mode, stage, graves }, ...(adBonus ? { adBonus } : {}) };
    },
    run(state, { index, battle, adBonus }, ctx) {
      const p = state.player;
      const W = ctx.defs.WEAPONS_DEF;
      if (!(GRAVE_MODES as readonly string[]).includes(battle.mode)) throw new RuleError('not_available', { mode: battle.mode });
      const teamKey = (battle.mode == 'missions' ? 'mission' : battle.mode) + battle.stage;
      if (!Object.prototype.hasOwnProperty.call(ctx.defs.TEAMS_DEF, teamKey)) throw new RuleError('unknown_item', { field: 'battle.stage', id: teamKey });
      if (battle.graves.length == 0 || index > battle.graves.length) throw new RuleError('unknown_item', { field: 'index', index, graves: battle.graves.length });
      const grave = battle.graves[index - 1];
      if (grave.searched) throw new RuleError('already_searched', { index });

      const found = getGraveResult(ctx, grave.cpu_level, battle.mode, battle.stage, adBonus === true);
      const result: GraveResult = { index, ptype: found.ptype, id: found.id };
      if (found.ptype == '') return { state, result };
      const id = found.id;
      if (found.ptype == 'weapon') {
        if (Boolean(p.weapons[id]) && p.weapons[id][0] < W[id].limit) {
          ++p.weapons[id][0];
          p.weapons[id][p.weapons[id][0]] = { range: 1, stolen: false, crafted: false };
        } else if (!p.weapons[id]) {
          p.weapons[id] = [1, { range: 1, stolen: false, crafted: false }];
        }
        weaponOptimisation(p.weapons);
      } else if (found.ptype == 'obj') {
        if (Boolean(p.objs[id]) && p.objs[id] < GRAVE_ITEM_CAP) ++p.objs[id];
        else if (!p.objs[id]) p.objs[id] = 1;
      } else if (found.ptype == 'stim') {
        if (Boolean(p.stim_behave[id]) && p.stim_behave[id] < GRAVE_ITEM_CAP) ++p.stim_behave[id];
        else if (!p.stim_behave[id]) p.stim_behave[id] = 1;
      } else if (found.ptype == 'gold') {
        p.gold = int(p.gold + Number(id));
      } else if (found.ptype == 'diam') {
        p.diam = int(p.diam + Number(id));
      }
      if (!adBonus) p.tired += 3;
      return { state, result };
    },
  }),
};
